import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { FaEnvelope, FaPaperPlane, FaTimes, FaKey } from "react-icons/fa";
import { useToastHelpers } from "../Toasts/ToastUtils";
import { forgotPassword } from "../../apis/UserApis";
import { useTheme } from "../Theme/ThemeContext";

const isValidEmail = (value) => {
  const v = String(value || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
};

export default function ForgotPasswordModal({ show, onClose }) {
  const { successToast, errorToast } = useToastHelpers();
  const { currentTheme } = useTheme();

  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const emailOk = useMemo(() => isValidEmail(email), [email]);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!emailOk) {
      setFormError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    try {
      await forgotPassword({ email: email.trim() });
      successToast("Reset email sent", "Check your inbox for the reset link");
      onClose?.();
    } catch (err) {
      console.error("Forgot password failed:", err);
      const status = err?.response?.status;

      const backendMsg = String(err?.response?.data?.message || err?.response?.data?.error || "").toLowerCase();

      if (status === 400) {
        if (backendMsg.includes("must be a valid email") || backendMsg.includes("validation error")) {
          setFormError("Please enter a valid email address.");
          errorToast("Invalid email", "Please check and try again");
        } else if (backendMsg.includes("not registered") || backendMsg.includes("not found") || backendMsg.includes("does not exist")) {
          setFormError("No account found with this email. Please check and try again.");
          errorToast("Email not registered");
        } else {
          setFormError("Couldn't send the reset email. Please verify your email and try again.");
          errorToast("Couldn't send reset email");
        }
        return;
      }

      setFormError("Failed to send email. Please try again in a moment.");
      errorToast("Failed to send email, please try again");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.2 }}
        className="glass rounded-3xl shadow-2xl border border-white/20 dark:border-white/10 p-6 sm:p-8 max-w-md w-full relative overflow-hidden"
      >
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-accent-100 to-emerald-100 dark:from-accent-900/20 dark:to-emerald-900/20 rounded-full filter blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2"></div>
        
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
          aria-label="Close"
        >
          <FaTimes className="w-4 h-4" />
        </button>

        {/* Icon */}
        <div className={`w-14 h-14 bg-gradient-to-br ${currentTheme.colors.gradient} rounded-2xl flex items-center justify-center mb-5 shadow-lg ${currentTheme.colors.glow}`}>
          <FaKey className="w-6 h-6 text-white" />
        </div>

        <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">Forgot Password?</h2>
        <p className="text-neutral-600 dark:text-neutral-300 mb-6">
          No worries! Enter your email and we'll send you a reset link.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">Email Address</label>
            <div className="relative">
              <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (formError) setFormError("");
                }}
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700 focus:bg-white dark:focus:bg-neutral-600 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 transition-all outline-none text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500"
                placeholder="user@example.com"
                autoFocus
              />
            </div>
            {formError ? (
              <p className="text-xs text-red-600 dark:text-red-400 mt-2 font-medium">{formError}</p>
            ) : !emailOk && email.trim().length > 0 ? (
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                Example: <span className="font-medium">name@gmail.com</span>
              </p>
            ) : null}
          </div>

          <button
            type="submit"
            disabled={!emailOk || isSubmitting}
            className={`w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-semibold text-white bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 shadow-lg ${currentTheme.colors.glow} hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <FaPaperPlane className="w-4 h-4" />
            {isSubmitting ? "Sending..." : "Send Reset Link"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
